import React, { useCallback, useEffect, useMemo, useState } from "react";
import toast from "react-hot-toast";

import {
  createCompanyInstitution,
  deleteCompanyInstitution,
  getApiErrorMessage,
  listCompanyInstitutions,
  listDepartments,
  updateCompanyInstitution,
} from "../../services/adminCompanyService.js";
import { listPortalAlerts } from "../../services/sisdeuAdminService.js";
import InstitutionAccessModal from "./InstitutionAccessModal.jsx";
import SisdeuAlertsModal from "./SisdeuAlertsModal.jsx";
import SisdeuInstitutionLinksModal from "./SisdeuInstitutionLinksModal.jsx";
import SisdeuPolicyModal from "./SisdeuPolicyModal.jsx";
import { dedupeAlertsById } from "./sisdeuAdmin.utils.js";

const EMPTY_FORM = {
  name: "",
  abrev: "",
  address: "",
  siteweb: "",
  iddepartamento: "",
};

const MAX_IMAGE_BYTES = 5 * 1024 * 1024;
const ALLOWED_IMAGE_TYPES = new Set([
  "image/jpeg",
  "image/png",
  "image/webp",
  "image/gif",
  "image/avif",
]);

const getInstitutionImageUrl = (institution) => {
  if (institution?.image) return institution.image;

  const imagePath = String(institution?.image_path || "").trim();
  if (!imagePath) return "";
  if (/^https?:\/\//i.test(imagePath)) return imagePath;

  const pathBase = String(institution?.path_base || "").trim();
  if (!pathBase) return imagePath;

  return `${pathBase.replace(/\/+$/, "")}/${imagePath.replace(/^\/+/, "")}`;
};

const CompanyInstitutionsTab = ({ idcompany }) => {
  const [institutions, setInstitutions] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [form, setForm] = useState(EMPTY_FORM);
  const [editing, setEditing] = useState(null);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState("");
  const [imageInputKey, setImageInputKey] = useState(0);
  // Institución cuyo modal "Accesos" (cuentas de delegado, FTR-0013) está abierto.
  const [accessInstitution, setAccessInstitution] = useState(null);
  // Cuenta a la que abrir directo "Accesos" al llegar desde una alerta SISDEU (FTR-0017 §7).
  const [focusAccountId, setFocusAccountId] = useState(null);
  // FTR-0017 Phase 4: administración SISDEU de la compañía (vínculos, política, alertas).
  const [sisdeuLinksOpen, setSisdeuLinksOpen] = useState(false);
  const [sisdeuPolicyOpen, setSisdeuPolicyOpen] = useState(false);
  const [sisdeuAlertsOpen, setSisdeuAlertsOpen] = useState(false);
  const [openAlertsCount, setOpenAlertsCount] = useState(0);

  const loadInstitutions = useCallback(async () => {
    if (!idcompany) return;
    setLoading(true);
    try {
      setInstitutions(await listCompanyInstitutions(idcompany, true));
    } catch (error) {
      console.error("Error al cargar instituciones", error);
      toast.error(getApiErrorMessage(error, "No se pudieron cargar las instituciones."));
    } finally {
      setLoading(false);
    }
  }, [idcompany]);

  useEffect(() => {
    loadInstitutions();
  }, [loadInstitutions]);

  // Cambiar de compañía efectiva cierra el modal de accesos de la compañía anterior.
  useEffect(() => {
    setAccessInstitution(null);
    setFocusAccountId(null);
  }, [idcompany]);

  // Insignia de alertas SISDEU abiertas (FTR-0017 §7). Tolerante en silencio: si el módulo está apagado
  // para esta compañía o el admin no tiene acceso, simplemente no se muestra insignia — no es un error
  // digno de un toast en cada carga de la pestaña.
  const refreshOpenAlertsCount = useCallback(async () => {
    if (!idcompany) return;
    try {
      const { alerts } = await listPortalAlerts("open");
      setOpenAlertsCount(dedupeAlertsById(alerts).length);
    } catch {
      setOpenAlertsCount(0);
    }
  }, [idcompany]);

  useEffect(() => {
    refreshOpenAlertsCount();
  }, [refreshOpenAlertsCount]);

  useEffect(() => {
    listDepartments()
      .then(setDepartments)
      .catch((error) => console.error("Error al cargar departamentos", error));
  }, []);

  useEffect(() => {
    return () => {
      if (imagePreview.startsWith("blob:")) URL.revokeObjectURL(imagePreview);
    };
  }, [imagePreview]);

  const filteredInstitutions = useMemo(() => {
    const term = search.trim().toLowerCase();
    if (!term) return institutions;
    return institutions.filter((institution) =>
      [institution.name, institution.abrev, institution.address].some((value) =>
        String(value || "").toLowerCase().includes(term)
      )
    );
  }, [institutions, search]);

  const resetForm = () => {
    setForm(EMPTY_FORM);
    setEditing(null);
    setImageFile(null);
    setImagePreview("");
    setImageInputKey((current) => current + 1);
  };

  const beginEdit = (institution) => {
    setEditing(institution);
    setForm({
      name: institution.name || "",
      abrev: institution.abrev || "",
      address: institution.address || "",
      siteweb: institution.siteweb || "",
      iddepartamento:
        institution.iddepartamento != null
          ? String(institution.iddepartamento)
          : "",
    });
    setImageFile(null);
    setImagePreview(getInstitutionImageUrl(institution));
    setImageInputKey((current) => current + 1);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleChange = (event) => {
    const { name, value } = event.target;
    setForm((current) => ({ ...current, [name]: value }));
  };

  const handleImageChange = (event) => {
    const file = event.target.files?.[0] || null;

    if (!file) {
      setImageFile(null);
      setImagePreview(getInstitutionImageUrl(editing));
      return;
    }

    if (!ALLOWED_IMAGE_TYPES.has(file.type)) {
      toast.error("Formato no permitido. Use JPG, JPEG, PNG, WEBP, GIF o AVIF.");
      event.target.value = "";
      setImageFile(null);
      setImagePreview(getInstitutionImageUrl(editing));
      return;
    }

    if (file.size > MAX_IMAGE_BYTES) {
      toast.error("La imagen no puede superar los 5 MB.");
      event.target.value = "";
      setImageFile(null);
      setImagePreview(getInstitutionImageUrl(editing));
      return;
    }

    setImageFile(file);
    setImagePreview(URL.createObjectURL(file));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!form.name.trim()) {
      toast.error("El nombre de la institución es obligatorio.");
      return;
    }

    const payload = new FormData();
    Object.entries({
      idcompany,
      name: form.name.trim(),
      abrev: form.abrev.trim(),
      address: form.address.trim(),
      siteweb: form.siteweb.trim(),
      iddepartamento: form.iddepartamento,
      ...(editing ? { status: Number(editing.status ?? 1) } : {}),
    }).forEach(([key, value]) => payload.append(key, value ?? ""));
    if (imageFile) payload.append("image", imageFile);

    setSaving(true);
    try {
      if (editing) {
        await updateCompanyInstitution({
          idinstitution: editing.idinstitution,
          payload,
        });
        toast.success("Institución actualizada correctamente.");
      } else {
        await createCompanyInstitution(payload);
        toast.success("Institución creada correctamente.");
      }
      resetForm();
      await loadInstitutions();
    } catch (error) {
      toast.error(getApiErrorMessage(error, "No se pudo guardar la institución."));
    } finally {
      setSaving(false);
    }
  };

  const handleStatusChange = async (institution) => {
    const isActive = Number(institution.status) !== 0;
    const action = isActive ? "desactivar" : "reactivar";
    if (!window.confirm(`¿Desea ${action} ${institution.name}?`)) return;

    setSaving(true);
    try {
      if (isActive) {
        await deleteCompanyInstitution(institution.idinstitution);
      } else {
        await updateCompanyInstitution({
          idinstitution: institution.idinstitution,
          payload: { idcompany, status: 1 },
        });
      }
      toast.success(`Institución ${isActive ? "desactivada" : "reactivada"}.`);
      if (editing?.idinstitution === institution.idinstitution) resetForm();
      await loadInstitutions();
    } catch (error) {
      toast.error(
        getApiErrorMessage(error, `No se pudo ${action} la institución.`)
      );
    } finally {
      setSaving(false);
    }
  };

  // Navegación directa desde una alerta SISDEU (FTR-0017 §7): abre "Accesos" ya enfocado en la cuenta.
  const handleNavigateToAccount = ({ idinstitution, idaccount }) => {
    const target = institutions.find((item) => Number(item.idinstitution) === Number(idinstitution));
    if (!target) {
      toast.error("No se pudo ubicar la institución en la lista actual.");
      return;
    }
    setSisdeuAlertsOpen(false);
    setFocusAccountId(idaccount);
    setAccessInstitution(target);
  };

  return (
    <div className="grid grid-cols-1 gap-5 xl:grid-cols-[minmax(320px,0.8fr)_minmax(0,1.2fr)]">
      <section className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm dark:border-slate-600 dark:bg-gray-700">
        <div className="mb-4 flex items-start justify-between gap-3">
          <div>
            <h2 className="text-xl font-bold text-slate-800 dark:text-white">
              {editing ? "Editar institución" : "Nueva institución"}
            </h2>
            <p className="text-sm text-slate-500 dark:text-slate-300">
              La institución creada será asociada a la organización.
            </p>
          </div>
          {editing && (
            <button
              type="button"
              onClick={resetForm}
              className="text-sm font-semibold text-blue-600 hover:text-blue-800"
            >
              Nueva
            </button>
          )}
        </div>

        <form onSubmit={handleSubmit} className="space-y-3">
          <label className="block text-sm font-medium text-slate-700 dark:text-slate-200">
            Nombre *
            <input
              name="name"
              value={form.name}
              onChange={handleChange}
              required
              className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 font-normal text-slate-700 focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-200"
            />
          </label>
          <label className="block text-sm font-medium text-slate-700 dark:text-slate-200">
            Abreviatura
            <input
              name="abrev"
              value={form.abrev}
              onChange={handleChange}
              className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 font-normal text-slate-700 focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-200"
            />
          </label>
          <label className="block text-sm font-medium text-slate-700 dark:text-slate-200">
            Dirección
            <input
              name="address"
              value={form.address}
              onChange={handleChange}
              className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 font-normal text-slate-700 focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-200"
            />
          </label>
          <label className="block text-sm font-medium text-slate-700 dark:text-slate-200">
            Sitio web
            <input
              type="url"
              name="siteweb"
              value={form.siteweb}
              onChange={handleChange}
              placeholder="https://..."
              className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 font-normal text-slate-700 focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-200"
            />
          </label>
          <div>
            <label className="block text-sm font-medium text-slate-700 dark:text-slate-200">
              {editing ? "Reemplazar imagen" : "Imagen de la institución"}
              <input
                key={imageInputKey}
                type="file"
                name="image"
                accept=".jpg,.jpeg,.png,.webp,.gif,.avif,image/jpeg,image/png,image/webp,image/gif,image/avif"
                onChange={handleImageChange}
                className="mt-1 block w-full rounded-lg border border-slate-300 bg-white text-sm font-normal text-slate-600 file:mr-4 file:border-0 file:bg-blue-50 file:px-4 file:py-2 file:font-semibold file:text-blue-700 hover:file:bg-blue-100"
              />
            </label>
            <p className="mt-1 text-xs text-slate-500 dark:text-slate-300">
              JPG, JPEG, PNG, WEBP, GIF o AVIF. Máximo 5 MB.
            </p>
            {imagePreview && (
              <div className="mt-3 flex h-36 items-center justify-center overflow-hidden rounded-lg border border-slate-200 bg-slate-50">
                <img
                  src={imagePreview}
                  alt="Vista previa de la institución"
                  className="h-full w-full object-contain"
                />
              </div>
            )}
          </div>
          <label className="block text-sm font-medium text-slate-700 dark:text-slate-200">
            Departamento
            <select
              name="iddepartamento"
              value={form.iddepartamento}
              onChange={handleChange}
              className="mt-1 w-full rounded-lg border border-slate-300 bg-white px-3 py-2 font-normal text-slate-700 focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-200"
            >
              <option value="">Sin departamento</option>
              {departments.map((department) => (
                <option
                  key={department.iddepartamento}
                  value={department.iddepartamento}
                >
                  {department.name}
                </option>
              ))}
            </select>
          </label>
          <div className="flex justify-end gap-2 pt-2">
            {editing && (
              <button
                type="button"
                onClick={resetForm}
                className="rounded-lg border border-slate-300 px-4 py-2 text-sm font-semibold text-slate-600 hover:bg-slate-50"
              >
                Cancelar
              </button>
            )}
            <button
              type="submit"
              disabled={saving}
              className="rounded-lg bg-blue-600 px-4 py-2 text-sm font-semibold text-white hover:bg-blue-700 disabled:opacity-60"
            >
              {saving ? "Guardando..." : editing ? "Guardar cambios" : "Crear institución"}
            </button>
          </div>
        </form>
      </section>

      <section className="min-w-0 rounded-xl border border-slate-200 bg-white p-5 shadow-sm dark:border-slate-600 dark:bg-gray-700">
        <div className="mb-4">
          <h2 className="text-xl font-bold text-slate-800 dark:text-white">
            Instituciones de la compañía
          </h2>
          <input
            type="search"
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            placeholder="Buscar institución..."
            className="mt-3 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-200"
          />
          {/* FTR-0017 Phase 4: administración SISDEU de la compañía, dentro de la misma pestaña. */}
          <div className="mt-3 flex flex-wrap gap-2">
            <button
              type="button"
              onClick={() => setSisdeuLinksOpen(true)}
              className="rounded-lg border border-slate-300 px-3 py-1.5 text-xs font-semibold text-slate-700 hover:bg-slate-50"
            >
              Vínculos SISDEU
            </button>
            <button
              type="button"
              onClick={() => setSisdeuPolicyOpen(true)}
              className="rounded-lg border border-slate-300 px-3 py-1.5 text-xs font-semibold text-slate-700 hover:bg-slate-50"
            >
              Política SISDEU
            </button>
            <button
              type="button"
              onClick={() => setSisdeuAlertsOpen(true)}
              className="relative rounded-lg border border-slate-300 px-3 py-1.5 text-xs font-semibold text-slate-700 hover:bg-slate-50"
            >
              Alertas SISDEU
              {openAlertsCount > 0 && (
                <span className="ml-1.5 inline-flex min-w-5 items-center justify-center rounded-full bg-red-600 px-1.5 py-0.5 text-[0.65rem] font-black text-white">
                  {openAlertsCount > 99 ? "99+" : openAlertsCount}
                </span>
              )}
            </button>
          </div>
        </div>

        {loading ? (
          <div className="py-10 text-center text-sm text-slate-500">Cargando...</div>
        ) : filteredInstitutions.length === 0 ? (
          <div className="rounded-lg border border-dashed border-slate-300 py-10 text-center text-sm text-slate-500">
            No hay instituciones registradas.
          </div>
        ) : (
          <div className="space-y-2">
            {filteredInstitutions.map((institution) => {
              const active = Number(institution.status) !== 0;
              return (
                <article
                  key={institution.idinstitution}
                  className={`flex flex-col justify-between gap-3 rounded-lg border p-3 sm:flex-row sm:items-center ${
                    active
                      ? "border-slate-200 bg-white"
                      : "border-slate-200 bg-slate-50 opacity-70"
                  }`}
                >
                  <div className="min-w-0">
                    <div className="flex flex-wrap items-center gap-2">
                      <h3 className="truncate font-semibold text-slate-800">
                        {institution.name}
                      </h3>
                      {institution.abrev && (
                        <span className="rounded bg-slate-100 px-2 py-0.5 text-xs text-slate-500">
                          {institution.abrev}
                        </span>
                      )}
                      {!active && (
                        <span className="rounded-full bg-red-50 px-2 py-0.5 text-xs font-semibold text-red-600">
                          Inactiva
                        </span>
                      )}
                    </div>
                    <p className="truncate text-sm text-slate-500">
                      {institution.address || "Sin dirección"}
                    </p>
                  </div>
                  <div className="flex shrink-0 gap-2">
                    <button
                      type="button"
                      onClick={() => beginEdit(institution)}
                      className="rounded-lg border border-blue-200 px-3 py-1.5 text-xs font-semibold text-blue-700 hover:bg-blue-50"
                    >
                      Editar
                    </button>
                    <button
                      type="button"
                      onClick={() => setAccessInstitution(institution)}
                      className="rounded-lg border border-slate-300 px-3 py-1.5 text-xs font-semibold text-slate-700 hover:bg-slate-50"
                    >
                      Accesos
                    </button>
                    <button
                      type="button"
                      onClick={() => handleStatusChange(institution)}
                      disabled={saving}
                      className={`rounded-lg border px-3 py-1.5 text-xs font-semibold ${
                        active
                          ? "border-red-200 text-red-600 hover:bg-red-50"
                          : "border-emerald-200 text-emerald-700 hover:bg-emerald-50"
                      }`}
                    >
                      {active ? "Desactivar" : "Reactivar"}
                    </button>
                  </div>
                </article>
              );
            })}
          </div>
        )}
      </section>

      {accessInstitution && (
        <InstitutionAccessModal
          key={accessInstitution.idinstitution}
          institution={accessInstitution}
          focusAccountId={focusAccountId}
          onClose={() => {
            setAccessInstitution(null);
            setFocusAccountId(null);
            refreshOpenAlertsCount();
          }}
        />
      )}

      {sisdeuLinksOpen && (
        <SisdeuInstitutionLinksModal
          onClose={() => {
            setSisdeuLinksOpen(false);
            refreshOpenAlertsCount();
          }}
        />
      )}

      {sisdeuPolicyOpen && <SisdeuPolicyModal onClose={() => setSisdeuPolicyOpen(false)} />}

      {sisdeuAlertsOpen && (
        <SisdeuAlertsModal
          onClose={() => setSisdeuAlertsOpen(false)}
          onNavigateToAccount={handleNavigateToAccount}
          onChanged={refreshOpenAlertsCount}
        />
      )}
    </div>
  );
};

export default CompanyInstitutionsTab;
